import { ModuleLabel } from '../module-label';
import { useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ModuleManagementDataContext } from '../../providers/module-management-provider';
import { useApi } from '../../providers/api-provider';
import { useAuth } from '../../providers/auth-provider';
import { ModuleUsageDto, ModuleUsageRelation } from '../../general/module-usage';
import '../../assets/module-usage.css';

const courseLink = (key: string) => `/admin/courses?${new URLSearchParams({ course: key })}`;
function Relations({ title, empty, rows }: { title: string; empty: string; rows: ModuleUsageRelation[] }) {
    const catalog = useContext(ModuleManagementDataContext)?.allModules;
    return <section aria-label={title}><h3>{title}</h3><DataTable value={rows} dataKey="relation_key" emptyMessage={empty} sortField="module_name" sortOrder={1}>
        <Column field="module_name" header="Module" sortable body={(row: ModuleUsageRelation) => <Link to={`/admin/modules?${new URLSearchParams({ module: row.module_key, tab: 'usage' })}`}><ModuleLabel name={row.module_name} managed={catalog?.find(m => m.module_key === row.module_key)?.is_managed} /></Link>} />
        <Column field="course_name" header="Applies in" sortable body={(row: ModuleUsageRelation) => row.course_key
            ? <Link to={courseLink(row.course_key)}>{row.course_name}</Link> : 'All courses / direct assignments'} />
        <Column header="Requirement" body={(row: ModuleUsageRelation) => row.is_optional ? 'Optional' : 'Required'} />
    </DataTable></section>;
}

export function ModuleUsage() {
    const ctx = useContext(ModuleManagementDataContext)!;
    const { selectedModules, mode, selectedModuleFull } = ctx;
    const api = useApi(), identity = useAuth().getUser()?.sub;
    const key = selectedModules.length === 1 && mode !== 'create' ? selectedModules[0].module_key : null;
    const [revision, setRevision] = useState(0);
    const [snapshot, setSnapshot] = useState<{ key: string; data: ModuleUsageDto } | null>(null);
    const [error, setError] = useState('');
    const requestKey = JSON.stringify([identity, key]);
    const data = snapshot?.key === requestKey ? snapshot.data : null;
    useEffect(() => {
        setSnapshot(null); setError('');
        if (!key || !api) return;
        const controller = new AbortController();
        api.getModuleUsage(key, controller.signal).then(result => {
            if (!controller.signal.aborted) setSnapshot({ key: requestKey, data: result });
        }).catch(() => { if (!controller.signal.aborted) setError('Could not load module usage. Please try again.'); });
        return () => controller.abort();
    }, [api, key, requestKey, revision]);
    if (mode === 'create') return <p>Save the module before checking where it is used.</p>;
    if (!key) return <p>{selectedModules.length ? 'Select one module to view its usage.' : 'No module selected.'}</p>;
    return <div className="module-usage">
        <header className="admin-toolbar justify-content-between"><h2>Usage &amp; dependencies</h2>
            <Button label="Refresh" icon="pi pi-refresh" text onClick={() => setRevision(value => value + 1)} /></header>
        <p className="text-color-secondary">Saved relationships for this module. Changing a shared module affects every course using it.</p>
        {mode === 'edit' && <p role="status">This view shows saved relationships. Save module changes to update them.</p>}
        {error ? <div role="alert">{error} <Button label="Retry" text onClick={() => setRevision(value => value + 1)} /></div>
            : !data ? <p role="status">Loading module usage…</p> : <>
                <section aria-label="Used in courses"><h3>Used in courses</h3>
                    <DataTable value={data.courses} dataKey="course_key" emptyMessage="This module is not included in any courses." sortField="course_name" sortOrder={1}>
                        <Column field="course_name" header="Course" sortable body={(row: ModuleUsageDto['courses'][number]) => <Link to={courseLink(row.course_key)}>{row.course_name}</Link>} />
                        <Column field="position" header="Module position" sortable />
                        <Column field="learner_count" header="Enrolled learners" sortable />
                    </DataTable>
                    <small>Enrolled learners have a current course assignment. Counts exclude deleted users and removed assignments.</small>
                </section>
                <Relations title="This module requires…" empty="This module has no prerequisites." rows={data.requires} />
                <Relations title="These modules require this module…" empty="No other modules require this module." rows={data.required_by} />
                {selectedModuleFull?.module_key === key && <p className="text-color-secondary">Edit prerequisites in the module editor or in the relevant course.</p>}
            </>}
    </div>;
}
